<template>
  <div class="landing-page flex min-h-screen w-full flex-col bg-background-light dark:bg-background-dark text-slate-900 dark:text-slate-100 font-display">
    <LandingHeader />
    <main class="flex flex-col items-center flex-1 overflow-x-hidden min-w-0">
      <LandingHero />
      <LandingDemoShowcase />
      <AsyncLandingLogos />
      <AsyncLandingStats />
      <AsyncLandingFeatures />
      <AsyncPricingTable />
      <AsyncLandingFaq />
      <AsyncLandingCta />
    </main>
    <AsyncLandingFooter />
  </div>
</template>

<script setup>
import { defineAsyncComponent, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import LandingHeader from '@/components/landing/LandingHeader.vue'
import LandingHero from '@/components/landing/LandingHero.vue'
import LandingDemoShowcase from '@/components/landing/LandingDemoShowcase.vue'

const route = useRoute()
const { t, locale } = useI18n()

function setHeadMetaByName(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute('name', name)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function setHeadMetaByProperty(property, content) {
  let el = document.querySelector(`meta[property="${property}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute('property', property)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

/** Keep document title, description, and social/AI preview tags aligned with hero copy (locale changes). */
function syncLandingHead() {
  if (route.name !== 'Landing') return
  const titleCore = `${t('landing.hero.titleBefore')}${t('landing.hero.titleHighlight')}`.trim()
  const pageTitle = titleCore ? `${titleCore} · QRestoo` : 'QRestoo'
  document.title = pageTitle
  const desc = t('landing.hero.subtitle')
  let meta = document.querySelector('meta[name="description"]')
  if (!meta) {
    meta = document.createElement('meta')
    meta.setAttribute('name', 'description')
    document.head.appendChild(meta)
  }
  meta.setAttribute('content', desc)

  const canonical =
    document.querySelector('link[rel="canonical"]')?.getAttribute('href') || `${window.location.origin}/`
  setHeadMetaByProperty('og:title', pageTitle)
  setHeadMetaByProperty('og:description', desc)
  setHeadMetaByProperty('og:url', canonical)
  setHeadMetaByName('twitter:title', pageTitle)
  setHeadMetaByName('twitter:description', desc)
}

onMounted(() => {
  syncLandingHead()
})
watch([locale, () => route.name], syncLandingHead)

// Lazy-load below-fold sections for better LCP and initial bundle size.
// Names prefixed with Async* so they never collide with auto-imports (e.g. LandingLogos.vue → LandingLogos).
const AsyncLandingLogos = defineAsyncComponent(() => import('@/components/landing/LandingLogos.vue'))
const AsyncLandingStats = defineAsyncComponent(() => import('@/components/landing/LandingStats.vue'))
const AsyncLandingFeatures = defineAsyncComponent(() => import('@/components/landing/LandingFeatures.vue'))
const AsyncPricingTable = defineAsyncComponent(() => import('@/components/landing/PricingTable.vue'))
const AsyncLandingFaq = defineAsyncComponent(() => import('@/components/landing/LandingFaq.vue'))
const AsyncLandingCta = defineAsyncComponent(() => import('@/components/landing/LandingCta.vue'))
const AsyncLandingFooter = defineAsyncComponent(() => import('@/components/landing/LandingFooter.vue'))
</script>

<style scoped>
.landing-page {
  font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
}
</style>
